import React from "react";
import dayjs, { Dayjs } from "dayjs";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EuroIcon from "@mui/icons-material/Euro";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import InsightsIcon from "@mui/icons-material/Insights";

import WorkDay from "../../Types/WorkDay";
import Enumerable from "linq";
import WorkDayService from "../../Services/WorkDayService";

import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import WorkDayRepository from "../../Repositories/WorkDayRepository";
import LocationRepository from "../../Repositories/LocationRepository";

import ErrorDialogue from "../../Components/ErrorDialogue/ErrorDialogue";
import CustomDateOverview from "../../Components/DateOverview/CustomDateOverview";
import WorkDayWorkersOverview from "../../Components/WorkDayTipOverview/WorkDayWorkersOverview";
import WorkDayTipOverview from "../../Components/WorkDayTipOverview/WorkDayTipOverview";
import PinCashOverviewGraph from "../../Components/Graphs/PinCashOverviewGraph";
import Spinner from "../../Components/Spinner/Spinner";

const ACCENT = "#c79656";

interface State {
    selectedDate: Dayjs | null;
    selectedMonth: Dayjs;
    selectedWorkPeriodId: string | null;
    loadedWorkDays: WorkDay[];
    loadedWorkDay: WorkDay | null;
    locationName: string;
    showError: boolean;
    errorTitle: string;
    errorMsg: string;
    isLoading: boolean;
}

export default class DashboardPage extends React.Component<{}, State> {
    static contextType = AuthContext;
    context!: AuthContextType;

    constructor(props: any) {
        super(props);

        this.onSelectedDateChange = this.onSelectedDateChange.bind(this);
        this.onSelectedMonthChanged = this.onSelectedMonthChanged.bind(this);
        this.onWorkDayDateChanged = this.onWorkDayDateChanged.bind(this);
        this.onWorkDayUpdated = this.onWorkDayUpdated.bind(this);

        this.state = {
            selectedDate: dayjs(),
            selectedMonth: dayjs().startOf("month"),
            selectedWorkPeriodId: null,
            loadedWorkDays: [],
            loadedWorkDay: null,
            locationName: "",
            showError: false,
            errorTitle: "",
            errorMsg: "",
            isLoading: false,
        };
    }

    public componentDidMount() {
        this.setState({ isLoading: true });
        Promise.all([this.loadLocationName(), this.loadWorkDays()]).finally(() =>
            this.setState({ isLoading: false })
        );
    }

    private async loadLocationName() {
        const result = await LocationRepository.getLocationName(this.context.locationId);
        if (result.isFailed()) {
            this.setState({ errorTitle: result.errorTitle ?? "", errorMsg: result.errorMessage ?? "", showError: true });
            return;
        }
        this.setState({ locationName: result.result! });
    }

    private async loadWorkDays() {
        const { selectedMonth, selectedDate } = this.state;
        const result = await WorkDayRepository.getWorkedDays(
            this.context.locationId,
            selectedMonth.startOf("month"),
            selectedMonth.endOf("month")
        );
        if (result.isFailed()) {
            this.setState({ errorTitle: result.errorTitle ?? "", errorMsg: result.errorMessage ?? "", showError: true });
            return;
        }
        const loadedWorkDays = result.result!.workDays.map(
            (x: any) => new WorkDay(
                x.id,
                dayjs(x.date).format("YYYY-MM-DD"),
                x.pinTip ?? 0,
                x.cashTip ?? 0,
                x.isClosed,
                x.countedState,
                x.countedBy ?? [],
                x.workers ?? [],
                x.workPeriods ?? [],
            )
        );

        this.setState({ loadedWorkDays }, async () => {
            await this.onSelectedDateChange(selectedDate ?? dayjs());
        });
    }

    private async onSelectedDateChange(value: Dayjs) {
        const summary = Enumerable.from(this.state.loadedWorkDays).firstOrDefault(x => dayjs(x.Date).isSame(value, "day")) ?? null;
        let workDay = summary;

        if (summary != null) {
            const result = await WorkDayRepository.getWorkDayByDate(this.context.locationId, value);
            if (result.isFailed()) {
                this.setState({ errorTitle: result.errorTitle ?? "", errorMsg: result.errorMessage ?? "", showError: true });
                return;
            }
            workDay = result.result ?? summary;
        }

        workDay = workDay != null ? this.normalizeWorkDay(workDay) : null;

        this.setState({
            selectedDate: value,
            loadedWorkDay: workDay,
            selectedWorkPeriodId: this.getPreferredPeriodId(workDay, null),
        });
    }

    private onWorkDayUpdated(newWorkDay: WorkDay) {
        const normalized = this.normalizeWorkDay(newWorkDay);
        this.applyWorkDayUpdate(normalized);
        this.refreshUpdatedWorkDay(normalized);
    }

    private onWorkDayDateChanged(from: Dayjs, to: Dayjs) {
        const existing = Enumerable.from(this.state.loadedWorkDays).firstOrDefault(x => dayjs(x.Date).isSame(from));
        if (existing == null) return;
        const targetDate = to.format("YYYY-MM-DD");
        const workPeriods = (existing.WorkPeriods ?? []).map((period) => {
            const moved = WorkDayService.cloneWorkPeriod(period);
            moved.Date = targetDate;
            return moved;
        });
        const updated = this.normalizeWorkDay(new WorkDay(
            existing.Id,
            targetDate,
            existing.TipPin,
            existing.TipCash,
            existing.IsClosed,
            existing.CountedState,
            [...(existing.CountedBy ?? [])],
            [...(existing.Workers ?? [])],
            workPeriods,
        ));
        this.setState(prev => ({
            loadedWorkDays: this.upsertLoadedWorkDay(prev.loadedWorkDays, updated, prev.selectedMonth, from),
            loadedWorkDay: updated,
            selectedDate: to,
        }));
    }

    private onSelectedMonthChanged(newMonth: Dayjs) {
        this.setState({ selectedMonth: newMonth }, () => this.loadWorkDays());
    }

    private normalizeWorkDay(workDay: WorkDay) {
        const date = dayjs(workDay.Date).format("YYYY-MM-DD");
        const workPeriods = (workDay.WorkPeriods ?? []).map((period) => {
            const clone = WorkDayService.cloneWorkPeriod(period);
            const periodDate = dayjs(clone.Date);
            clone.Date = periodDate.isValid() ? periodDate.format("YYYY-MM-DD") : date;
            return clone;
        });

        return new WorkDay(
            workDay.Id,
            date,
            workDay.TipPin ?? 0,
            workDay.TipCash ?? 0,
            workDay.IsClosed,
            workDay.CountedState,
            [...(workDay.CountedBy ?? [])],
            [...(workDay.Workers ?? [])],
            workPeriods,
        );
    }

    private upsertLoadedWorkDay(workDays: WorkDay[], updated: WorkDay, selectedMonth: Dayjs, previousDate?: Dayjs) {
        const updatedDate = dayjs(updated.Date);
        const withoutUpdated = workDays.filter((workDay) => {
            const sameId = updated.Id !== "" && workDay.Id === updated.Id;
            const sameDate = dayjs(workDay.Date).isSame(updatedDate, "day");
            const samePreviousDate = previousDate != null && dayjs(workDay.Date).isSame(previousDate, "day");
            return !sameId && !sameDate && !samePreviousDate;
        });

        if (!updatedDate.isSame(selectedMonth, "month")) {
            return withoutUpdated;
        }

        return [...withoutUpdated, updated];
    }

    private applyWorkDayUpdate(workDay: WorkDay) {
        this.setState(prev => ({
            loadedWorkDays: this.upsertLoadedWorkDay(prev.loadedWorkDays, workDay, prev.selectedMonth),
            loadedWorkDay: workDay,
            selectedWorkPeriodId: this.getPreferredPeriodId(workDay, prev.selectedWorkPeriodId),
        }));
    }

    private async refreshUpdatedWorkDay(workDay: WorkDay) {
        const result = await WorkDayRepository.getWorkDayByDate(this.context.locationId, dayjs(workDay.Date));
        if (result.isFailed()) {
            this.setState({ errorTitle: result.errorTitle ?? "", errorMsg: result.errorMessage ?? "", showError: true });
            return;
        }

        this.applyWorkDayUpdate(this.normalizeWorkDay(result.result ?? workDay));
    }

    private getPreferredPeriodId(workDay: WorkDay | null, currentId: string | null) {
        const periods = WorkDayService.getDisplayPeriods(workDay);
        if (periods.length === 0) return null;
        if (currentId != null && periods.some((period) => period.Id === currentId)) return currentId;
        return periods[0].Id;
    }

    private formatCurrency(n: number) {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
    }

    private KpiTile({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
        return (
            <div
                role="region"
                aria-label={label}
                style={{
                    background: "#fff",
                    borderRadius: 12,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    padding: "10px 14px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderTop: `3px solid ${ACCENT}`,
                    transition: "box-shadow 160ms ease, transform 160ms ease",
                    minHeight: 80,
                    boxSizing: "border-box",
                }}
                onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 22px rgba(0,0,0,0.1)";
                    (e.currentTarget as HTMLDivElement).style.transform = "translateY(-1px)";
                }}
                onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)";
                    (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: `${ACCENT}18`, border: `1px solid ${ACCENT}33`,
                        display: "grid", placeItems: "center", color: ACCENT,
                    }}>
                        {icon}
                    </div>
                    <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.6px", color: "rgba(26,26,26,0.55)", fontWeight: 600 }}>
                        {label}
                    </span>
                </div>
                <div style={{
                    fontSize: 22, fontWeight: 600, lineHeight: 1.1,
                    fontVariantNumeric: "tabular-nums",
                    background: `linear-gradient(160deg, #1f2937 0%, ${ACCENT} 200%)`,
                    WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                }}>
                    {value}
                </div>
            </div>
        );
    }

    private Card({ title, style, children }: { title: string; style?: React.CSSProperties; children?: React.ReactNode }) {
        return (
            <div
                role="region"
                aria-label={title}
                style={{
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                    padding: 16,
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    overflow: "hidden",
                    minHeight: 0,
                    ...style,
                }}
            >
                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    {children}
                </div>
            </div>
        );
    }

    public render() {
        const { loadedWorkDays, loadedWorkDay, selectedDate, selectedWorkPeriodId, locationName, isLoading, showError, errorTitle, errorMsg } = this.state;

        const visibleWorkDays = loadedWorkDays.filter((workDay) => WorkDayService.hasWorkDayData(workDay));
        const daysWorked = visibleWorkDays.length;
        const totalCash  = visibleWorkDays.reduce((s, d) => s + (d.IsClosed ? 0 : (d.TipCash || 0)), 0);
        const totalPin   = visibleWorkDays.reduce((s, d) => s + (d.IsClosed ? 0 : (d.TipPin  || 0)), 0);
        const avgPerDay  = daysWorked ? (totalCash + totalPin) / daysWorked : 0;

        return (
            <div style={{
                width: "100%", height: "100%",
                boxSizing: "border-box",
                padding: 10,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                overflow: "visible",
            }}>
                {isLoading && <Spinner />}

                <ErrorDialogue title={errorTitle} message={errorMsg} onClose={() => this.setState({ showError: false })} show={showError} />

                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    <div style={{ width: 6, height: 36, background: ACCENT, borderRadius: 6 }} />
                    <span style={{ fontSize: 26, fontWeight: 800, color: "#1a1a1a", letterSpacing: 0.2 }}>
                        {locationName || "Dashboard"}
                    </span>
                </div>

                {/* KPI row */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 12,
                    flexShrink: 0,
                }}>
                    {this.KpiTile({ label: "Worked days",     value: daysWorked.toString(),          icon: <CalendarMonthIcon fontSize="small" /> })}
                    {this.KpiTile({ label: "Total cash",      value: this.formatCurrency(totalCash), icon: <EuroIcon fontSize="small" /> })}
                    {this.KpiTile({ label: "Total pin",       value: this.formatCurrency(totalPin),  icon: <CreditCardIcon fontSize="small" /> })}
                    {this.KpiTile({ label: "Avg / worked day",value: this.formatCurrency(avgPerDay), icon: <InsightsIcon fontSize="small" /> })}
                </div>

                {/* Main grid — fills remaining viewport height */}
                <div style={{
                    flex: 1,
                    minHeight: 0,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gridTemplateRows: "0.92fr 1.08fr",
                    gap: 16,
                }}>
                    {/* Graph — col 1–2, row 1 */}
                    <this.Card title="Tip this month" style={{ gridColumn: "1 / 3", gridRow: "1 / 2" }}>
                        <PinCashOverviewGraph days={visibleWorkDays} />
                    </this.Card>

                    {/* Calendar — col 3, row 1 */}
                    <this.Card title="Calendar" style={{ gridColumn: "3 / 4", gridRow: "1 / 2" }}>
                        <CustomDateOverview
                            workDays={loadedWorkDays}
                            onDaySelected={(day: Dayjs) => this.onSelectedDateChange(day)}
                            onMonthChanged={(month: Dayjs) => this.onSelectedMonthChanged(month)}
                            onWorkDayMoved={(from: Dayjs, to: Dayjs) => this.onWorkDayDateChanged(from, to)}
                        />
                    </this.Card>

                    {/* Earned tip — col 1, row 2 */}
                    <this.Card title="Earned tip" style={{ gridColumn: "1 / 2", gridRow: "2 / 3" }}>
                        <WorkDayTipOverview
                            workDay={loadedWorkDay ?? selectedDate ?? dayjs()}
                            selectedPeriodId={selectedWorkPeriodId}
                            onSelectedPeriodChange={(periodId) => this.setState({ selectedWorkPeriodId: periodId })}
                            onUpdate={(newWorkDay: WorkDay) => this.onWorkDayUpdated(newWorkDay)}
                        />
                    </this.Card>

                    {/* Workers — col 2–3, row 2 */}
                    <this.Card title="Workers" style={{ gridColumn: "2 / 4", gridRow: "2 / 3" }}>
                        <WorkDayWorkersOverview
                            workDay={loadedWorkDay}
                            selectedDate={selectedDate}
                            selectedPeriodId={selectedWorkPeriodId}
                            onUpdated={(newWorkDay: WorkDay) => this.onWorkDayUpdated(newWorkDay)}
                        />
                    </this.Card>
                </div>
            </div>
        );
    }
}
